import { Translate2 } from 'common'
import {
  AvailabilityError,
  AvailabilityErrorType,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { CantBeFulfilledException } from '../exceptions/CantBeFulfilledException'
import miscellaneousVehicle from 'src/features/search/Results/utils/miscellaneousVehicle'
import { defaultDeliveryDetails } from '../helpers/orderTypes'
import {
  ISeller,
  SellerCartFields,
} from 'src/services/MultiSellerServicesProvider'
import { Vehicle } from 'src/store/models/Vehicles'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from 'src/ui-components/util/Interfaces'
import { RootState, store } from 'store'
import {
  allowOrderWithoutAvailableInventory,
  buildAvailabilityError,
  currentLocationHasQuantity,
  getLocationById,
  isCartVehicleEmpty,
  isSameVehicle,
  isSameVehicleShort,
  productsMatch,
  selectLocationByQty,
  validatePrices,
} from '../utils/Utils_testable'
import {
  CartProduct,
  IBundleProduct,
  IRemovePartFromCartProps,
  ISellerPart,
  ISetQtyPropsNew,
  PartsLocations,
  SellerCartVehicle,
  SellerOrderCartData,
  SellerWithLocations,
} from './ISellerCart'
import { cartSlice, ICartSlice, setSellerData } from './cartStore'
import { Transport } from 'src/api/cart/interfaces'
import {
  getPartsAvailability,
  isBuyDirectLocation,
  isCartSellerProduct,
  isVehicleComplete,
  updateLocationInfo,
} from '../Utils'
import { SellerPartInfoResponse } from 'src/store/cart/ValidationInterfaces'
import {
  buildAvailabilityRequest,
  productCostWithPriceBreaks,
  validatePricesWithPriceBreaks,
} from '../utils/CartValidations'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import {
  trackAddToCart,
  trackRemoveFromCart,
  updateBundleFulfilmentThunk,
} from '../utils/Utils'
import { setProcessingOrder } from './uiStore'

export const getCartStorageKey = (userId: string): string => `cart_${userId}`

export const findCartVehicle = (
  state: ICartSlice,
  vehicle: Vehicle
): SellerCartVehicle | undefined => {
  return state.cartData.vehicles.find((cartVehicle) =>
    isSameVehicle(cartVehicle.vehicle, vehicle)
  )
}

export const createCart = (
  state: ICartSlice,
  payload: {
    vehicle: Vehicle
    seller?: ISeller
    location?: ProductLocationModel
  }
): void => {
  const { vehicle, location, seller } = payload

  const vehicleToSet = isVehicleComplete(vehicle)
    ? vehicle
    : miscellaneousVehicle

  // Check if the same vehicle is already present in the cart.
  const vehicleInCart = findCartVehicle(state, vehicleToSet)
  if (!vehicleInCart) {
    state.cartData.vehicles.unshift({
      vehicle: vehicleToSet,
      specifications: [],
      laborResults: [],
      orderFormData: {
        poNumber: '',
        noteToStore: '',
        customerName: '',
        personalNote: '',
        shipToAddress: '',
      },
      sellers: [],
      quoteSourceId: '',
    })
  }
  if (!seller) {
    return
  }
  const cartVehicle = state.cartData.vehicles.find((cartVehicle) =>
    isSameVehicle(cartVehicle.vehicle, vehicleToSet)
  )
  if (!cartVehicle) {
    return
  }
  const cartSeller = cartVehicle.sellers.find(
    (cartSeller) => cartSeller.coverageKey === seller.coverageKey
  )
  if (!cartSeller) {
    cartVehicle.sellers.push({
      ...seller,
      locations: location
        ? [{ ...location, parts: [], deliveryDetail: defaultDeliveryDetails }]
        : [],
      noteToStore: '',
      poNumber: '',
    })
    return
  }
  if (!location) {
    return
  }
  const cartLocation = cartSeller.locations.find(
    (cartLocation) =>
      cartLocation.locationId.toString() === location.locationId.toString()
  )
  if (!cartLocation) {
    cartSeller.locations.push({
      ...location,
      parts: [],
      deliveryDetail: defaultDeliveryDetails,
    })
  }
}

export const removeProductFromSeller = (
  state: ICartSlice,
  payload: {
    product: ProductModel
    vehicle: Vehicle
    sellerId: string
    excludeLocations: string[]
    cleanup: boolean
    showToast: boolean
    currentVehicle: Vehicle
    trackRemoveFromCart: (part: ProductModel, vehicle: Vehicle) => void
  }
): void => {
  const {
    cleanup = true,
    excludeLocations = [],
    product,
    sellerId,
    showToast = true,
    vehicle,
    currentVehicle,
    trackRemoveFromCart,
  } = payload
  const cartVehicle = state.cartData.vehicles.find((cartVehicle) =>
    isSameVehicle(cartVehicle.vehicle, vehicle)
  )
  if (!cartVehicle) {
    return
  }
  const cartSeller = cartVehicle.sellers.find(
    (seller) => seller.coverageKey === sellerId
  )
  if (!cartSeller) {
    return
  }

  cartSeller.locations.forEach((location) => {
    if (excludeLocations.includes(location.locationId.toString())) {
      return
    }
    removeProductFromLocation(
      product,
      location,
      vehicle,
      showToast,
      trackRemoveFromCart
    )
  })
  if (cleanup) {
    cleanupCart(state, currentVehicle)
  }
  state.cartData = { ...state.cartData }
}

export const cleanupCart = (
  state: ICartSlice,
  currentVehicle: Vehicle
): void => {
  // Cleanup parts with zero quantity.
  for (const vehicle of state.cartData.vehicles) {
    for (const seller of vehicle.sellers) {
      for (const location of seller.locations) {
        location.parts = location.parts.filter(
          (part) => part.quantityRequested > 0
        )
      }
    }
  }
  // Cleanup empty locations.
  for (const vehicle of state.cartData.vehicles) {
    for (const seller of vehicle.sellers) {
      seller.locations = seller.locations.filter(
        (location) => location.parts.length
      )
    }
  }

  // Cleanup empty sellers.
  for (const vehicle of state.cartData.vehicles) {
    vehicle.sellers = vehicle.sellers.filter(
      (seller) => seller.locations.length
    )
  }

  // Cleanup empty Vehicles.
  state.cartData.vehicles = state.cartData.vehicles.filter((vehicle) => {
    return (
      !isCartVehicleEmpty(vehicle) ||
      isSameVehicle(vehicle.vehicle, currentVehicle)
    )
  })
  // Reset error display.
  state.displayCartFormErrors = false
}

export const clearSpecsAndLaborAfterOrder = (state: ICartSlice): void => {
  // Clear laborResults, specifications, and shipToAddress from vehicles that have no parts remaining
  for (const vehicle of state.cartData.vehicles) {
    const hasParts = vehicle.sellers.some((seller) =>
      seller.locations.some((location) => location.parts.length > 0)
    )

    if (!hasParts) {
      vehicle.laborResults = []
      vehicle.specifications = []
      vehicle.orderFormData = {
        ...vehicle.orderFormData,
        shipToAddress: '',
        customerName: '',
      }
    }
  }
}

export const removeProductFromLocation = (
  product: ProductModel,
  location: PartsLocations,
  vehicle: Vehicle,
  showToast = true,
  trackRemoveFromCart: (part: ProductModel, vehicle: Vehicle) => void
): void => {
  location.parts = location?.parts?.filter((part) => {
    if (productsMatch(part, product)) {
      trackRemoveFromCart(part, vehicle)
      if (showToast) {
        ShowNotification({
          message: Translate2('partWasRemovedFromYourCart', [
            product.description || '',
          ]),
          variant: ToastNotificationVariant.SUCCESS,
        })
      }
      return false
    }
    return true
  })
}

const assertLocationAndQuantity = (
  locationId: string | undefined,
  quantity: number
): void => {
  if (locationId === undefined || locationId === '') {
    throw new Error('No location selected')
  }
  if (quantity <= 0) {
    throw new Error('Invalid quantity')
  }
}

const throwIfCannotFulfillAtAnyLocation = (
  updatedProduct: CartProduct,
  quantity: number,
  firstLocationWithQty: ProductLocationModel,
  orderIfNotAvailable: boolean | undefined,
  validate: boolean
): void => {
  const cannotFulfill =
    firstLocationWithQty.qtyAvailable < quantity &&
    !allowOrderWithoutAvailableInventory(
      firstLocationWithQty,
      orderIfNotAvailable
    ) &&
    !IS_INTEGRATED_VERSION

  if (!cannotFulfill) {
    return
  }

  if (validate) {
    updatedProduct.errors.push(
      buildAvailabilityError(AvailabilityErrorType.CANT_BE_FULFILLED)
    )
  }
  throw new CantBeFulfilledException('productCantBeFulfilled')
}

const resolveAutoChangedLocationId = (
  state: ICartSlice,
  updatedProduct: CartProduct,
  payload: {
    locationId: string
    quantity: number
    seller: ISeller
    validate: boolean
    vehicle: Vehicle
    orderIfNotAvailable?: boolean
    onLocationChange?: (location: ProductLocationModel) => void
  }
): string => {
  const {
    locationId,
    quantity,
    seller,
    validate,
    vehicle,
    orderIfNotAvailable,
    onLocationChange,
  } = payload

  const firstLocationWithQty = selectLocationByQty(
    updatedProduct,
    quantity,
    true
  )

  /* ALS(II)/ALS(II.a)/ALS(II.a2) */
  throwIfCannotFulfillAtAnyLocation(
    updatedProduct,
    quantity,
    firstLocationWithQty,
    orderIfNotAvailable,
    validate
  )

  /* ALS(I): keep manual location when it has qty; otherwise override ALS(I.b) */
  const shouldOverrideLocation =
    firstLocationWithQty.locationId !== locationId &&
    !currentLocationHasQuantity(updatedProduct, quantity, locationId)

  if (!shouldOverrideLocation) {
    return locationId
  }

  removeProductFromSeller(state, {
    product: updatedProduct,
    vehicle,
    sellerId: seller.coverageKey,
    excludeLocations: [],
    cleanup: true,
    showToast: false,
    currentVehicle: miscellaneousVehicle,
    trackRemoveFromCart: () => {
      //
    },
  })
  if (onLocationChange) {
    onLocationChange(firstLocationWithQty)
  }
  if (validate) {
    updatedProduct.errors.push(
      buildAvailabilityError(AvailabilityErrorType.LOCATION_OVERRIDE)
    )
  }
  return firstLocationWithQty.locationId
}

const applyInsufficientAvailabilityErrors = (
  updatedProduct: CartProduct,
  location: ProductLocationModel,
  quantity: number,
  orderIfNotAvailable: boolean | undefined,
  validate: boolean
): void => {
  if (location.qtyAvailable >= quantity) {
    return
  }

  const canOrderWithoutInventory = allowOrderWithoutAvailableInventory(
    location,
    orderIfNotAvailable
  )

  if (!canOrderWithoutInventory && !IS_INTEGRATED_VERSION) {
    const availableElsewhere = updatedProduct.location.some(
      (item) => item.qtyAvailable >= quantity
    )
    updatedProduct.errors.push(
      buildAvailabilityError(
        validate && !availableElsewhere
          ? AvailabilityErrorType.CANT_BE_FULFILLED
          : AvailabilityErrorType.CANT_BE_FULFILLED_FROM_SELECTED_LOCATION
      )
    )
  }

  if (orderIfNotAvailable) {
    updatedProduct.errors.push(
      buildAvailabilityError(AvailabilityErrorType.INSUFFICIENT_QTY)
    )
    return
  }

  const shouldValidateOutOfStock =
    validate ||
    StoreInstances.userStore.preferences.cart_validateOutOfStock?.toLowerCase() ===
      'true'

  if (canOrderWithoutInventory && shouldValidateOutOfStock) {
    updatedProduct.errors.push(
      buildAvailabilityError(AvailabilityErrorType.OUT_OF_STOCK_ALL_LOCATIONS)
    )
  }
}

const assertQuantityMeetsMinimum = (
  location: ProductLocationModel,
  quantity: number,
  validate: boolean,
  updatedProduct: CartProduct
): void => {
  const { minQty } = location
  if (quantity >= (minQty ?? 1) || quantity === 0) {
    return
  }
  if (validate) {
    updatedProduct.errors.push(
      buildAvailabilityError(AvailabilityErrorType.MIN_QTY_CHANGE)
    )
  }
  throw new Error('quantityRequestedIsLessThanMinimum')
}

export const setQtyAtLocation = (
  state: ICartSlice,
  payload: ISetQtyPropsNew
): void => {
  const {
    locationId,
    product,
    quantity,
    seller,
    validate,
    vehicle,
    autoLocationChange,
    orderIfNotAvailable,
    onLocationChange,
    originalLocation,
  } = payload

  assertLocationAndQuantity(locationId, quantity)

  const updatedProduct: CartProduct = {
    ...product,
    errors: [],
    quantityRequested: quantity,
    bundleInfo: [],
  }

  const shouldAutoChangeLocation = autoLocationChange && !orderIfNotAvailable
  const locationToSet = shouldAutoChangeLocation
    ? resolveAutoChangedLocationId(state, updatedProduct, {
        locationId,
        quantity,
        seller,
        validate,
        vehicle,
        orderIfNotAvailable,
        onLocationChange,
      })
    : locationId

  const location = getLocationById(updatedProduct, locationToSet)

  applyInsufficientAvailabilityErrors(
    updatedProduct,
    location,
    quantity,
    orderIfNotAvailable,
    validate
  )

  // Commented this functionality, as the position of the parts are changing when quantity is updated - bug ticket MPV3-6990.
  // const productInCart = this.isProductInSellerCart(
  //   updatedProduct,
  //   vehicle,
  //   seller.coverageKey
  // )
  // if (productInCart) {
  //   this.removeProductFromSeller(updatedProduct, vehicle, seller.coverageKey)
  // } else {
  //   sellerStoreInstance.trackAddToCart(updatedProduct, vehicle, location, quantity)
  // }

  trackAddToCart(updatedProduct, vehicle, location, quantity)

  if (validate) {
    const priceValidations = validatePrices(originalLocation, location)
    updatedProduct.errors = priceValidations
  }

  // const validatePrimaryLocation =
  //   StoreInstances.userStore.preferences.cart_nonPrimaryLocWarningEnabled?.toLowerCase() ===
  //   'true'

  // if (validatePrimaryLocation) {
  //   const validatedProductLocation = this.validateLocation(
  //     updatedProduct,
  //     location?.locationId
  //   )
  //   updatedProduct.errors = validatedProductLocation.errors
  // }

  assertQuantityMeetsMinimum(location, quantity, validate, updatedProduct)

  addOrUpdateProductInCart(state, {
    product: updatedProduct,
    location,
    vehicle,
    seller,
  })
}

const addOrUpdateProductInCart = (
  state: ICartSlice,
  payload: {
    product: CartProduct
    location: ProductLocationModel
    vehicle: Vehicle
    seller: ISeller
  }
): void => {
  const { location, product, seller, vehicle } = payload
  createCart(state, { vehicle, seller, location })

  const cartVehicle = state.cartData.vehicles.find((cartVehicle) =>
    isSameVehicle(cartVehicle.vehicle, vehicle)
  )
  if (!cartVehicle) {
    throw new Error('Vehicle not present in cart')
  }
  const cartSeller = cartVehicle.sellers.find(
    (cartSeller) => cartSeller.coverageKey === seller.coverageKey
  )
  if (!cartSeller) {
    throw new Error('Seller not present in cart')
  }

  const cartLocation = cartSeller.locations.find(
    (cartLocation) =>
      cartLocation.locationId.toString() === location.locationId.toString()
  )
  if (!cartLocation) {
    throw new Error('Location not present in cart')
  }

  const updatedProduct = {
    ...product,
    location: product.location.map((loc) => ({
      ...loc,
      isSelected: loc.locationId.toString() === location.locationId.toString(),
    })),
  }

  const partIndex = cartLocation.parts.findIndex((cartPart) =>
    productsMatch(cartPart, updatedProduct)
  )

  const multiLocation = StoreInstances.uiStore.isMultiLocationEnabled()

  if (partIndex < 0) {
    cartLocation.parts.push(updatedProduct)
  } else {
    cartLocation.parts.splice(partIndex, 1, updatedProduct)
  }
  // MPV3-9212 enabled function to remove part from other locations when multiLocation is false.
  if (!multiLocation) {
    removeProductFromSeller(state, {
      product,
      vehicle,
      sellerId: seller.coverageKey,
      excludeLocations: [cartLocation.locationId.toString()],
      cleanup: true,
      showToast: false,
      currentVehicle: vehicle,
      trackRemoveFromCart: () => {
        //
      },
    })
  }

  cleanupCart(state, {})
}

export const updateBundleInfo = (
  state: ICartSlice,
  payload: SellerOrderCartData<IBundleProduct>[]
): void => {
  state.cartData.vehicles.forEach((cartVehicle) => {
    // Find the matching vehicle in payload
    const payloadVehicle = payload.find((vehicle) =>
      isSameVehicleShort(
        {
          id: cartVehicle.vehicle.id,
          year: cartVehicle.vehicle.year
            ? cartVehicle.vehicle.year?.value?.toString()
            : '',
          make: cartVehicle.vehicle.make
            ? cartVehicle.vehicle.make?.value?.toString()
            : '',
          model: cartVehicle.vehicle.model
            ? cartVehicle.vehicle.model?.value?.toString()
            : '',
          engine: cartVehicle.vehicle.engine
            ? cartVehicle.vehicle.engine?.value?.toString()
            : '',
        },
        vehicle.vehicle
      )
    )

    if (!payloadVehicle) {
      return
    }

    cartVehicle.sellers.forEach((seller) => {
      seller.locations.forEach((location) => {
        /* Match location using both locationId and seller coverageKey to handle multi-seller scenarios
        Prevents incorrect mapping when multiple sellers share the same locationId */

        const payloadLocation = payloadVehicle.locations.find(
          (payloadLocation) =>
            payloadLocation.locationId.toString() ===
              location.locationId.toString() &&
            (payloadLocation.coverageKey || '') === (seller.coverageKey || '')
        )

        location.parts.forEach((locationPart) => {
          // initialise new part and empty existing parts.
          locationPart.bundleInfo = []

          const payloadParts = (payloadLocation?.parts || []).filter((part) =>
            productsMatch({ ...part, location: [] }, locationPart)
          )
          if (payloadParts.length > 0) {
            payloadParts.forEach((payloadPart) => {
              locationPart.bundleInfo.push({
                ...payloadPart,
                // bundlePrice is null in case of non-bundled parts.
                bundlePrice: payloadPart.bundled
                  ? payloadPart.bundlePrice
                  : payloadPart.unitCostPrice || 0,
              })
            })
          } else {
            const partLocation = locationPart.location.find(
              (loc) =>
                loc.locationId.toString() === location.locationId.toString()
            )
            if (!partLocation) {
              return
            }
            locationPart.bundleInfo.push({
              bundleCode: null,
              bundled: false,
              bundleDiscount: null,
              bundleId: null,
              bundleName: null,
              bundlePrice: partLocation.cost || 0,
              isBuyDirectLocation: isBuyDirectLocation(partLocation),
              locationId: partLocation.locationId,
              orderType: '',
              snCalled: partLocation.snCalled || partLocation.called || '',
              packNumUnits: 0,
              packNumUnits100: 0,
              lineCode: locationPart.lineCode,
              partNumber: locationPart.partNumber,
              quantityRequested: locationPart.quantityRequested,
            })
          }
        })
      })
    })
  })
}

export const getUpdatedQtyAfterDeleting = (
  state: RootState,
  payload: IRemovePartFromCartProps
): number => {
  const vehicle = findCartVehicle(state.cart, payload.vehicle)
  if (!vehicle) return 0
  const seller = vehicle.sellers.find(
    (s) => s.coverageKey === payload.seller.coverageKey
  )
  if (!seller) return 0
  const location = seller.locations.find(
    (l) => l.locationId === payload.locationId
  )
  if (!location) return 0
  const part = location.parts.find((p) => productsMatch(p, payload.product))
  return part?.quantityRequested ?? 0
}

export const removeProductFromCartVehicle = (
  state: ICartSlice,
  payload: IRemovePartFromCartProps
): void => {
  const {
    product,
    quantity,
    seller,
    vehicle,
    locationId,
    removeFromAllLocations,
    showToastMessage = true, // shouldn't show message after placing order.
  } = payload

  const cartVehicle = findCartVehicle(state, vehicle)
  if (!cartVehicle) return

  const cartSeller = cartVehicle.sellers.find(
    (s) => s.coverageKey === (seller?.coverageKey ?? '')
  )
  if (!cartSeller) return

  // Remove from all locations
  if (removeFromAllLocations) {
    cartSeller.locations.forEach((location) => {
      const part = location.parts.find((p) => productsMatch(p, product))
      if (!part) return

      trackRemoveFromCart(part, vehicle, quantity)

      const newQuantity = part.quantityRequested - quantity
      part.quantityRequested = Math.max(0, newQuantity)
    })
    if (showToastMessage) {
      ShowNotification({
        message: Translate2('partWasRemovedFromYourCart', [
          product.description || '',
        ]),
        variant: ToastNotificationVariant.SUCCESS,
      })
    }

    cleanupCart(state, vehicle)
    return
  }

  // Remove from a single location (existing behavior)
  const location = cartSeller.locations.find(
    (l) => l.locationId.toString() === locationId
  )
  if (!location) return

  const part = location.parts.find((p) => productsMatch(p, product))
  if (!part) return

  trackRemoveFromCart(part, vehicle, quantity)

  const newQuantity = part.quantityRequested - quantity
  part.quantityRequested = Math.max(0, newQuantity)

  if (showToastMessage) {
    ShowNotification({
      message: Translate2('partWasRemovedFromYourCart', [
        product.description || '',
      ]),
      variant: ToastNotificationVariant.SUCCESS,
    })
  }

  cleanupCart(state, vehicle)
}

export const hasLaborOrSpecs = (vehicle: SellerCartVehicle): boolean =>
  (vehicle.laborResults?.length ?? 0) > 0 ||
  (vehicle.specifications?.length ?? 0) > 0

export const isValidSellerFormField = (
  field: string
): field is SellerCartFields => {
  return field === 'poNumber' || field === 'noteToStore'
}

export const hashTransport = (transport: Transport): string => {
  const seqNo = transport.seqNo?.toString()?.replace(/\s/g, '')?.toLowerCase()
  const carrier = transport.carrier?.replace(/\s/g, '')?.toLowerCase()
  const shipCode = transport.shipCode?.replace(/\s/g, '')?.toLowerCase()
  const releaseCode = transport.releaseCode?.replace(/\s/g, '')?.toLowerCase()
  return `${carrier}_${shipCode}_${releaseCode}_${seqNo}`
}

export const checkPriceChangesForCartProducts = (
  vehicles: SellerCartVehicle[]
): SellerCartVehicle[] => {
  return vehicles.filter((v) =>
    v.sellers.some((s) =>
      s.locations.some((l) =>
        l.parts.some((p) =>
          p.errors.some(
            (e) => e.errorType === AvailabilityErrorType.PRICE_OVERRIDE
          )
        )
      )
    )
  )
}

const getCartVehicle = (
  vehicles: SellerCartVehicle[],
  vehicle: Vehicle
): SellerCartVehicle | undefined => {
  return vehicles.find((cartVehicle) =>
    isSameVehicle(cartVehicle.vehicle, vehicle)
  )
}

const getCartSellerFromVehicles = (
  vehicles: SellerCartVehicle[],
  vehicle: Vehicle,
  sellerId: string
): SellerWithLocations | undefined => {
  const cartVehicle = getCartVehicle(vehicles, vehicle)
  if (!cartVehicle) {
    return undefined
  }
  return cartVehicle.sellers.find((seller) => seller.coverageKey === sellerId)
}

export const handleSellerPartQtyPriceChanges = (
  stateSlice: ICartSlice,
  cartProduct: CartProduct,
  sellerPartInfo: SellerPartInfoResponse
): void => {
  const orderIfNotAvailable =
    StoreInstances.userStore?.preferences?.findit_orderIfNotAvail === 'true'
  const insufficientQtyWarning =
    StoreInstances.userStore?.preferences?.insufficientQtyWarning === 'true'

  // Updating cart product with latest partInfoLocations immutably
  const updatedProduct: CartProduct = {
    ...cartProduct,
    ...updateLocationInfo(cartProduct, sellerPartInfo),
    errors: [...(cartProduct.errors || [])], // ensure new array
  }

  const partInfoLocations = updatedProduct.location || []
  const selectedLocationId = sellerPartInfo.location.locationId.toString()
  const requestedQty = cartProduct.quantityRequested

  const selectedLocation = partInfoLocations.find(
    (loc) => loc.locationId.toString() === selectedLocationId
  )
  const hasQtyAtSelectedLocation =
    (selectedLocation?.qtyAvailable ?? 0) >= requestedQty
      ? selectedLocation
      : undefined

  // Find a valid location based on orderIfNotAvailable preference
  const getValidLocation = (
    locations: ProductLocationModel[],
    requestedQty: number,
    orderIfNotAvailable: boolean
  ): ProductLocationModel | undefined => {
    if (orderIfNotAvailable) {
      if (
        insufficientQtyWarning &&
        selectedLocation &&
        (selectedLocation.qtyAvailable ?? 0) < requestedQty
      ) {
        updatedProduct.errors.push(
          buildAvailabilityError(AvailabilityErrorType.INSUFFICIENT_QTY)
        )
      }
      return selectedLocation
    }

    const selectedIndex = locations.findIndex(
      (loc) => loc.locationId.toString() === selectedLocationId
    )
    if (selectedIndex < 0) {
      return undefined
    }

    const lowerLocations = locations.slice(selectedIndex + 1)
    return (
      hasQtyAtSelectedLocation ??
      lowerLocations.find((loc) => loc.qtyAvailable >= requestedQty)
    )
  }

  // Function to handle errors and update cart immutably
  const updateCartWithLocation = (
    locationToSet: ProductLocationModel,
    selectedLocationId: string,
    updatedPart: CartProduct
  ): void => {
    const oldLocation = cartProduct.location.find(
      (loc) => loc.locationId.toString() === selectedLocationId
    )

    const priceValidations = validatePricesWithPriceBreaks(
      oldLocation,
      locationToSet,
      cartProduct.quantityRequested
    )

    // Build a new error array instead of mutating
    const newErrors: AvailabilityError[] = [
      ...updatedPart.errors,
      ...(locationToSet.locationId.toString() !== selectedLocationId
        ? [
            buildAvailabilityError(
              AvailabilityErrorType.CANT_BE_FULFILLED_FROM_SELECTED_LOCATION,
              undefined,
              oldLocation
            ),
          ]
        : []),
      ...(priceValidations || []),
    ]

    const updatedCartProduct: CartProduct = {
      ...updatedPart,
      errors: newErrors,
    }

    // Remove product if required
    if (
      locationToSet.locationId.toString() !== selectedLocationId &&
      (!orderIfNotAvailable ||
        (orderIfNotAvailable && !hasQtyAtSelectedLocation))
    ) {
      // Snapshot poNumber/noteToStore before removal — cleanupCart may remove
      // the seller entirely, and createCart re-creates it with empty defaults.
      const existingSeller = findCartVehicle(
        stateSlice,
        sellerPartInfo.vehicle
      )?.sellers.find(
        (s) => s.coverageKey === sellerPartInfo.seller.coverageKey
      )
      const preservedPoNumber = existingSeller?.poNumber ?? ''
      const preservedNoteToStore = existingSeller?.noteToStore ?? ''

      removeProductFromCartVehicle(stateSlice, {
        product: cartProduct,
        quantity: cartProduct.quantityRequested,
        locationId: sellerPartInfo.location.locationId.toString(),
        vehicle: sellerPartInfo.vehicle,
        seller: sellerPartInfo.seller,
        showToastMessage: false,
      })

      addOrUpdateProductInCart(stateSlice, {
        product: updatedCartProduct,
        location: locationToSet,
        vehicle: sellerPartInfo.vehicle,
        seller: sellerPartInfo.seller,
      })

      // Restore poNumber/noteToStore if they were wiped by the re-creation
      const restoredSeller = findCartVehicle(
        stateSlice,
        sellerPartInfo.vehicle
      )?.sellers.find(
        (s) => s.coverageKey === sellerPartInfo.seller.coverageKey
      )
      if (restoredSeller) {
        if (!restoredSeller.poNumber)
          restoredSeller.poNumber = preservedPoNumber
        if (!restoredSeller.noteToStore)
          restoredSeller.noteToStore = preservedNoteToStore
      }
      return
    }

    addOrUpdateProductInCart(stateSlice, {
      product: updatedCartProduct,
      location: locationToSet,
      vehicle: sellerPartInfo.vehicle,
      seller: sellerPartInfo.seller,
    })
  }

  if (selectedLocation) {
    const locationToSet = getValidLocation(
      partInfoLocations,
      requestedQty,
      orderIfNotAvailable
    )

    const effectiveCost = locationToSet
      ? productCostWithPriceBreaks(locationToSet, requestedQty)
      : 0
    const isZeroCost =
      effectiveCost === 0 && (locationToSet?.coreCost ?? 0) === 0

    if (!locationToSet || isZeroCost) {
      removeProductFromCartVehicle(stateSlice, {
        product: cartProduct,
        quantity: cartProduct.quantityRequested,
        locationId: sellerPartInfo.location.locationId.toString(),
        vehicle: sellerPartInfo.vehicle,
        seller: sellerPartInfo.seller,
      })

      // Re-fetch vehicle reference after removal so the mutation is on the live draft
      const cartVehicle = getCartVehicle(
        stateSlice.cartData.vehicles,
        sellerPartInfo.vehicle
      )
      if (cartVehicle) {
        const productWithError: CartProduct = {
          ...cartProduct,
          errors: [
            ...(cartProduct.errors || []),
            buildAvailabilityError(AvailabilityErrorType.CANT_BE_FULFILLED),
          ],
        }
        cartVehicle.productsCantbeFulfilled = [
          ...(cartVehicle.productsCantbeFulfilled || []),
          productWithError,
        ]
      }
    } else {
      updateCartWithLocation(locationToSet, selectedLocationId, updatedProduct)
    }
  }
}

interface SellerAvailabilityResult {
  parts: SellerPartInfoResponse[]
  hasTimeout: boolean
}

const getAllSellerPartsInfoResponse = (
  vehicles: SellerCartVehicle[]
): Promise<SellerAvailabilityResult>[] => {
  store.dispatch(setProcessingOrder(true))
  const allSellerPartInfoResponses: Promise<SellerAvailabilityResult>[] = []
  vehicles.forEach((v) => {
    v.sellers.forEach((s) => {
      const sellerParts: ISellerPart[] = []
      s.locations.forEach((l) => {
        l.parts.forEach((p) => {
          sellerParts.push({
            part: p,
            quantityRequested: p.quantityRequested,
            location: l,
          })
        })
      })

      // If there are parts for this seller, make an API request for the parts
      if (sellerParts.length > 0) {
        const sellerPartsAvailabilityReq = sellerParts.map(
          ({ part, quantityRequested, location }) =>
            buildAvailabilityRequest(part, quantityRequested, location)
        )

        // Call the API once for all parts under this seller
        const sellerAvailabilityPromise = getPartsAvailability(
          sellerPartsAvailabilityReq,
          s.coverageKey
        )
          .then((res) => {
            // Mapping the API response to include vehicle, seller, and location info
            const parts = res.parts.map((r, index) => {
              const { location } = sellerParts[index]
              return { ...r, vehicle: v.vehicle, seller: s, location }
            })
            return { parts, hasTimeout: res.hasTimeout }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(
              `Error fetching availability for seller ${s.sellerName}:`,
              error
            )
            return { parts: [], hasTimeout: false }
          })

        // Add the promise to the list of all promises
        allSellerPartInfoResponses.push(sellerAvailabilityPromise)
      }
    })
  })
  return allSellerPartInfoResponses
}

export const getPricesandAvailability = async (
  stateSlice: ICartSlice,
  sellerCartVehicle?: SellerCartVehicle
): Promise<{ hasTimeout: boolean }> => {
  const vehicles = sellerCartVehicle
    ? [sellerCartVehicle]
    : stateSlice.cartData.vehicles
  const allSellerPartsInfoResponse = getAllSellerPartsInfoResponse(vehicles)

  try {
    const results = await Promise.all(allSellerPartsInfoResponse)
    const hasTimeout = results.some((r) => r.hasTimeout)

    if (hasTimeout) return { hasTimeout }

    const sellerPartsInfo: SellerPartInfoResponse[] = results.flatMap(
      (r) => r.parts
    )

    sellerPartsInfo.forEach((sellerPartInfo) => {
      // 🔁 ALL reads & writes are against `vehicles` (the cloned cart)
      const seller = getCartSellerFromVehicles(
        vehicles,
        sellerPartInfo.vehicle,
        sellerPartInfo.seller.coverageKey
      )

      if (!seller) return

      const loc = seller.locations?.find(
        (l) => l.locationId === sellerPartInfo.location.locationId
      )

      if (!loc) return

      const cartProduct = loc.parts?.find((p) =>
        isCartSellerProduct(p, sellerPartInfo)
      )

      if (!cartProduct) return
      store.dispatch(
        cartSlice.actions.handleSellerPartQtyPriceChanges({
          cartProduct,
          sellerPartInfo,
        })
      )
    })
    store.dispatch(updateBundleFulfilmentThunk())
    return { hasTimeout }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error with some API calls:', error)
    return { hasTimeout: false }
  }
}

export const clearSpecsAndLaborIfNoParts = (): void => {
  const state = store.getState()
  const cartData = state.cart.cartData

  const updatedVehicles = cartData.vehicles.map((vehicle) => {
    const hasParts = vehicle.sellers.some((seller) =>
      seller.locations.some((location) => location.parts.length > 0)
    )

    if (!hasParts) {
      return {
        ...vehicle,
        laborResults: [],
        specifications: [],
        orderFormData: {
          ...vehicle.orderFormData,
          shipToAddress: '',
          customerName: '',
        },
      }
    }

    return vehicle
  })

  const updatedCartData = { ...cartData, vehicles: updatedVehicles }

  store.dispatch(setSellerData(updatedCartData))
}
